/**
 * Hengam elimina .lowStockDescriptionStyle del DOM si la variante no tiene low stock
 * y lo vuelve a crear al elegir otra variante. Este script:
 * - quita el clon cuando el nodo fuente no existe
 * - vuelve a clonarlo cuando Hengam lo regenera (observer + polling tras variant:change)
 */
(function () {
  const HENGAM_LOW_STOCK_MAX_MS = 30000;
  const HENGAM_LOW_STOCK_POLL_MS = 300;
  const HENGAM_LOW_STOCK_VARIANT_RETRY_MS = [0, 150, 400, 800, 1500, 2500, 4000];
  const HENGAM_LOW_STOCK_VARIANT_WATCH_MS = 150;
  const HENGAM_LOW_STOCK_VARIANT_WATCH_DURATION_MS = 6000;

  const SELECTORS = {
    productInfo: '.product__info-wrapper',
    sizeOptions: '.product-size-section .size-options',
    productForm: '#product-form',
    hengamSource:
      '.product__info-wrapper .lowStockDescriptionStyle:not([data-epic-hengam-low-stock-clone])',
    variantSelect: 'select[id^="VariantSelect-"]',
    variantOptions: '[data-option-index]',
  };

  const DATA = {
    clone: 'data-epic-hengam-low-stock-clone',
    spacer: 'data-epic-hengam-low-stock-spacer',
  };

  const LOW_STOCK_FONT_FAMILY = 'Syncopate, sans-serif';
  const LOW_STOCK_FONT_WEIGHT = '600';
  // A11y: WCAG 1.4.3 - el naranja original (#c77414) no pasa contraste (3.33:1 sobre fondo claro).
  // Usar un naranja más oscuro (#a8530e) que sí cumple 4.5:1 sin perder el "look" de aviso.
  const LOW_STOCK_COLOR = '#a8530e';

  let variantRetryTimers = [];
  let variantWatchInterval = null;
  let observerStarted = false;
  let syncScheduled = false;
  // Último HTML volcado al clon. Lo guardamos aquí en vez de leerlo del propio
  // clon porque el navegador normaliza el innerHTML al escribirlo (comillas,
  // orden de atributos), y entonces la comparación nunca daría igual.
  let lastClonedHtml = null;

  function getHengamLowStockSource() {
    return document.querySelector(SELECTORS.hengamSource);
  }

  function hengamLowStockHasMessage(hengamLowStockSource) {
    if (!hengamLowStockSource) return false;
    const message = hengamLowStockSource.querySelector('p')?.textContent?.trim();
    return Boolean(message || hengamLowStockSource.textContent.trim());
  }

  function removeEpicHengamLowStockClone(sizeSection) {
    sizeSection.querySelector(`[${DATA.clone}]`)?.remove();
    sizeSection.querySelector(`[${DATA.spacer}]`)?.remove();
    lastClonedHtml = null;
  }

  function applyLowStockStyles(epicHengamLowStockClone) {
    epicHengamLowStockClone.style.fontFamily = LOW_STOCK_FONT_FAMILY;
    epicHengamLowStockClone.style.fontWeight = LOW_STOCK_FONT_WEIGHT;
    epicHengamLowStockClone.style.setProperty('color', LOW_STOCK_COLOR, 'important');
    const lowStockParagraph = epicHengamLowStockClone.querySelector('p');
    lowStockParagraph?.style.setProperty('font-family', LOW_STOCK_FONT_FAMILY);
    lowStockParagraph?.style.setProperty('font-weight', LOW_STOCK_FONT_WEIGHT);
    // A11y: sobrescribir color del <p> interior para cumplir contraste WCAG 4.5:1
    lowStockParagraph?.style.setProperty('color', LOW_STOCK_COLOR, 'important');
  }

  function hideHengamLowStockOriginal(hengamLowStockSource) {
    const hengamLowStockPortal = hengamLowStockSource.closest('.nm-portal');
    if (!hengamLowStockPortal) return;
    hengamLowStockPortal.setAttribute('aria-hidden', 'true');
    hengamLowStockPortal.style.display = 'none';
  }

  function cloneHengamLowStockBesideSizes() {
    const productSizeOptionsEl = document.querySelector(SELECTORS.sizeOptions);
    if (!productSizeOptionsEl) return false;

    const sizeSection = productSizeOptionsEl.parentElement;
    const hengamLowStockSource = getHengamLowStockSource();

    // Hengam quitó el nodo del DOM → limpiar clon
    if (!hengamLowStockSource) {
      removeEpicHengamLowStockClone(sizeSection);
      return false;
    }

    // Nodo recién insertado pero aún sin texto → esperar siguiente sync
    if (!hengamLowStockHasMessage(hengamLowStockSource)) {
      sizeSection.querySelector(`[${DATA.clone}]`)?.remove();
      lastClonedHtml = null;
      hideHengamLowStockOriginal(hengamLowStockSource);
      return true;
    }

    let epicLowStockSpacer = sizeSection.querySelector(`[${DATA.spacer}]`);
    let epicHengamLowStockClone = sizeSection.querySelector(`[${DATA.clone}]`);

    if (!epicLowStockSpacer) {
      epicLowStockSpacer = document.createElement('br');
      epicLowStockSpacer.setAttribute(DATA.spacer, '');
      productSizeOptionsEl.after(epicLowStockSpacer);
    }

    if (!epicHengamLowStockClone) {
      epicHengamLowStockClone = hengamLowStockSource.cloneNode(false);
      epicHengamLowStockClone.className = hengamLowStockSource.className;
      epicHengamLowStockClone.setAttribute(DATA.clone, '');
      epicLowStockSpacer.after(epicHengamLowStockClone);
      lastClonedHtml = null;
    }

    // Solo escribimos si el mensaje cambió de verdad. El clon vive dentro del
    // subtree que observamos, así que volcar el mismo innerHTML dispara nuestro
    // propio observer, que reprograma otro volcado: un bucle a cada frame que
    // además hacía saltar sin parar al observer de la app de Hengam.
    const sourceHtml = hengamLowStockSource.innerHTML;
    if (sourceHtml !== lastClonedHtml) {
      epicHengamLowStockClone.innerHTML = sourceHtml;
      lastClonedHtml = sourceHtml;
      // Los estilos van aquí dentro: al reemplazar el innerHTML se recrea el
      // <p> interior y pierde lo que le hubiéramos puesto antes.
      applyLowStockStyles(epicHengamLowStockClone);
    }

    epicHengamLowStockClone.hidden = false;
    hideHengamLowStockOriginal(hengamLowStockSource);
    return true;
  }

  function scheduleCloneHengamLowStock() {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(() => {
      syncScheduled = false;
      cloneHengamLowStockBesideSizes();
    });
  }

  function startHengamLowStockVariantWatch() {
    clearInterval(variantWatchInterval);
    const watchDeadline = Date.now() + HENGAM_LOW_STOCK_VARIANT_WATCH_DURATION_MS;

    variantWatchInterval = setInterval(() => {
      cloneHengamLowStockBesideSizes();
      if (Date.now() > watchDeadline) {
        clearInterval(variantWatchInterval);
        variantWatchInterval = null;
      }
    }, HENGAM_LOW_STOCK_VARIANT_WATCH_MS);
  }

  function scheduleHengamLowStockSyncAfterVariantChange() {
    variantRetryTimers.forEach(clearTimeout);
    variantRetryTimers = HENGAM_LOW_STOCK_VARIANT_RETRY_MS.map((delayMs) =>
      setTimeout(cloneHengamLowStockBesideSizes, delayMs)
    );
    startHengamLowStockVariantWatch();
  }

  function observeHengamLowStockChanges() {
    if (observerStarted) return;
    observerStarted = true;

    const productInfoWrapper = document.querySelector(SELECTORS.productInfo);
    const productForm = document.querySelector(SELECTORS.productForm);

    const isInsideOwnClone = (node) => {
      const element = node?.nodeType === 1 ? node : node?.parentElement;
      return Boolean(element?.closest?.(`[${DATA.clone}]`));
    };

    const onDomChange = (mutations) => {
      // Si toda la tanda proviene de nuestro propio clon, no reprogramamos:
      // sería reaccionar a lo que acabamos de escribir nosotros.
      if (mutations.every((mutation) => isInsideOwnClone(mutation.target))) return;
      scheduleCloneHengamLowStock();
    };

    if (productInfoWrapper) {
      new MutationObserver(onDomChange).observe(productInfoWrapper, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    if (productForm) {
      new MutationObserver(onDomChange).observe(productForm, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    document.addEventListener('variant:change', scheduleHengamLowStockSyncAfterVariantChange);

    document.querySelector(SELECTORS.variantSelect)?.addEventListener(
      'change',
      scheduleHengamLowStockSyncAfterVariantChange
    );

    productInfoWrapper?.addEventListener(
      'change',
      (event) => {
        if (event.target.matches(SELECTORS.variantOptions)) {
          scheduleHengamLowStockSyncAfterVariantChange();
        }
      },
      true
    );
  }

  function initEpicHengamLowStockClone() {
    const pollDeadline = Date.now() + HENGAM_LOW_STOCK_MAX_MS;

    const pollUntilHengamLowStockReady = () => {
      cloneHengamLowStockBesideSizes();
      observeHengamLowStockChanges();

      if (getHengamLowStockSource() || Date.now() > pollDeadline) return;
      setTimeout(pollUntilHengamLowStockReady, HENGAM_LOW_STOCK_POLL_MS);
    };

    pollUntilHengamLowStockReady();
  }

  if (document.readyState === 'complete') {
    initEpicHengamLowStockClone();
  } else {
    window.addEventListener('load', initEpicHengamLowStockClone, { once: true });
  }
})();
